import { useRemoteData } from '@binhatch/hooks';
import { BonusValue, Card, PromotionTargetProgress, Select } from '@binhatch/ui';
import { getAllFromApi, getCurrentPeriod, getFallbackPeriod, getPromotionClientProgress, sortPeriods } from '@binhatch/utility';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ProgressStateAggregation, UserPromotion } from '@flexinet/flexinet-api';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';

import { productApi, progressApi } from '@/integrations/api';
import { translations } from '@binhatch/locale';

import { Configuration } from '@/containers/useConfiguration';

export const PromotionProgressPage: React.FC = () => {
  const promotion = useOutletContext<UserPromotion>();
  const intl = useIntl();
  const [{ value: configuration }] = Configuration.useContainer();

  const [periodId, setPeriodId] = React.useState(() => getCurrentPeriod(promotion.periods ?? [])?.id ?? getFallbackPeriod(promotion.periods ?? [])?.id);

  const period = React.useMemo(() => promotion.periods?.find((p) => p.id === periodId), [promotion.periods, periodId]);

  const periods = React.useMemo(() => sortPeriods(promotion.periods ?? []), [promotion.periods]);

  const progress = useRemoteData(
    { key: 'useProgressForPromotion', promotionId: promotion.id, periodId: period?.id, realtime: configuration.realtime },
    async ({ promotionId, periodId, realtime }) => {
      return await getAllFromApi(
        (nextToken) =>
          progressApi
            .listUserProgress([promotionId], periodId, nextToken, realtime ? ProgressStateAggregation.Total : ProgressStateAggregation.Confirmed)
            .then((r) => r.data),
        (r) => r.data
      );
    }
  );

  const levels = React.useMemo(() => {
    return promotion.target.levels.map((level, index) => ({
      index,
      level,
      ...getPromotionClientProgress(period, progress.data ?? [], level),
      claimed: false
    }));
  }, [progress, promotion, period]);

  return (
    <Card className="flex flex-col gap-8">
      <div className="flex items-center gap-8">
        <div className="font-semibold">
          <FormattedMessage id={translations.pages.promotionDetail.period.label} />
        </div>

        <Select
          containerClassName="w-64"
          contentClassName="w-full"
          items={
            periods
              ?.filter((period) => new Date(period.startAt).getTime() <= Date.now())
              .map((period) => ({
                value: period.id,
                name: intl.formatDateTimeRange(new Date(period.startAt), new Date(period.endAt), {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })
              })) ?? []
          }
          placeholder={intl.formatMessage({ id: translations.pages.promotionDetail.period.placeholder })}
          value={periodId}
          onChange={setPeriodId}
        />
      </div>

      <ul className="grid gap-4">
        {levels.map((progress, index) => (
          <li key={index}>
            <PromotionTargetProgress {...{ promotion, progress }}>
              <div className="flex flex-1 justify-end">
                <BonusValue
                  getProduct={(id) => productApi.getUserProductById(id).then((r) => r.data)}
                  reference={progress.level.bonus.ref}
                  unit={progress.level.bonus.mu}
                  value={progress.level.bonus.value}
                />
              </div>
            </PromotionTargetProgress>
          </li>
        ))}
      </ul>

      <div className="flex gap-2 rounded bg-slate-100 p-4">
        <InformationCircleIcon className="h-6 w-6 flex-shrink-0" />
        <FormattedMessage id={translations.pages.promotionDetail.note} />
      </div>
    </Card>
  );
};
